import React from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { FloatingInput, Button, ClosedSelect } from '@piplapps/pipl-ui';
import styles from '../../LeadVettingPage/components/LeadVettingFormStyles.module.less';
import signupStyles from './SignUp.module.less';
import { trackEvent } from '../../LeadVettingPage/utils';
import AnalyticsEventNames from '../../../Utils/AnalyticsHandler/AnalyticsEventNames';
import AnalyticsHandler from '../../../Utils/AnalyticsHandler/AnalyticsHandler';
import PageviewHandler from '../../../Utils/AnalyticsHandler/PageviewHandler';
import TermsCheckbox from '../../../Components/TermsCheckbox';
import { codeMap, displayUseCaseCodeMap } from '../../../Redux/modules/signup/constants';

const SignUpStep = (props) => {
  const validate = (values) => {};

  const formik = useFormik({
    initialValues: {
      firstName: props.prepopulatedFields.firstName,
      lastName: props.prepopulatedFields.lastName,
      email: props.prepopulatedFields.email,
      phone: props.prepopulatedFields.phone,
      useCaseCode: props.prepopulatedFields.useCaseCode,
      subUseCaseCode: props.prepopulatedFields.subUseCaseCode,
      useCaseText: props.prepopulatedFields.useCaseText,
      otherDescription: props.prepopulatedFields.otherDescription,
      terms: false,
      confirmUsage: false,
    },
    validate,
    onSubmit: (values) => {
      trackEvent('Submit', '');
      props.onSubmit(values);
    },
  });

  const termsError = formik.errors.terms && formik.touched.terms ? formik.errors.terms : null;
  const confirmUsageError =
    formik.errors.confirmUsage && formik.touched.confirmUsage ? formik.errors.confirmUsage : null;
  const useCase = displayUseCaseCodeMap[formik.values?.useCaseText] || '';

  const onConfirmUsageChange = (e) => {
    const isOn = formik.values.confirmUsage;
    trackEvent('confirmUsage', e.target.value);
    formik.setFieldValue('confirmUsage', !isOn);
    props.updateFields({ confirmUsage: formik.values.confirmUsage });
  };

  const contactEmails = props.prepopulatedFields.contactEmails || [];
  const isContactPickerDisabled = contactEmails.length <= 1;
  const contactEmailOptions = (
    contactEmails.length > 0 ? contactEmails : [props.prepopulatedFields.email].filter(Boolean)
  ).map((email) => ({ value: email, label: email }));

  const onContactEmailSelect = (selectedEmail) => {
    formik.setFieldValue('email', selectedEmail);
    formik.setFieldValue('firstName', '');
    formik.setFieldValue('lastName', '');
    formik.setFieldValue('phone', '');
  };

  return (
    <>
      <PageviewHandler path="signup-account-info/" />
      <div className={styles.form}>
        <div className={styles.headerWrapper}>
          <div className={styles.pageTitle}>
            <span>{gettext('Account Information')}</span>
          </div>
        </div>
        <div>
          <form onSubmit={formik.handleSubmit} id="sign-up-step-form">
            <div className={styles.fieldRow}>
              <FloatingInput
                id="id_firstName"
                name="firstName"
                placeholder={gettext('First Name')}
                testId="firstName-input"
                value={formik.values.firstName ? formik.values.firstName : ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                forcedError={formik.errors.firstName && formik.touched.firstName ? formik.errors.firstName : null}
                error={formik.errors.firstName}
              />
            </div>
            <div className={styles.fieldRow}>
              <FloatingInput
                id="id_lastName"
                name="lastName"
                placeholder={gettext('Last Name')}
                testId="lastName-input"
                value={formik.values.lastName ? formik.values.lastName : ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                forcedError={formik.errors.lastName && formik.touched.lastName ? formik.errors.lastName : null}
                error={formik.errors.lastName}
              />
            </div>
            <div className={styles.fieldRow}>
              <ClosedSelect
                id="id_email"
                name="email"
                label={gettext('Business Email')}
                testId="email-input"
                options={contactEmailOptions}
                setValue={(v) => v}
                height={45}
                onChange={onContactEmailSelect}
                value={formik.values.email ? formik.values.email : ''}
                error={formik.errors.email && formik.touched.email ? formik.errors.email : null}
                isDisabled={isContactPickerDisabled}
                disabled={isContactPickerDisabled}
              />
            </div>
            <div className={styles.fieldRow}>
              <FloatingInput
                id="id_phone"
                name="phone"
                placeholder={gettext('Phone Number')}
                testId="phone-input"
                value={formik.values.phone ? formik.values.phone : ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                forcedError={formik.errors.phone && formik.touched.phone ? formik.errors.phone : null}
                error={formik.errors.phone}
              />
            </div>
            <div className={styles.fieldRow}>
              <FloatingInput
                id="id_application"
                name="application"
                placeholder={gettext('Business Application')}
                testId="application-input"
                value={
                  formik.values?.useCaseText === codeMap.OTHER ? formik.values?.otherDescription || useCase : useCase
                }
                disabled
                size="wide"
                height={100}
                options={{ classes: signupStyles.application }}
              />
            </div>
            <TermsCheckbox
              error={termsError}
              product={props.product}
              onChange={(e) => {
                const isOn = formik.values.terms;
                trackEvent('terms', e.target.value);
                formik.setFieldValue('terms', !isOn);
                props.updateFields({ terms: formik.values.terms });
              }}
            />
            <div className={styles.fieldRow}>
              <div className={styles.checkboxSection}>
                <div>
                  <label htmlFor="confirm-usage-agree" className={styles.checkboxContainer}>
                    <span className={styles.checkboxLabel}>
                      {gettext('I agree to use Pipl data for the above Business Application')}
                    </span>
                    <input
                      type="checkbox"
                      name="confirmUsage"
                      onChange={onConfirmUsageChange}
                      checked={formik.values.confirmUsage}
                      test-id="input-confirm-usage"
                      id="confirm-usage-agree"
                    />
                    <span className={styles.checkmark} />
                  </label>
                </div>
              </div>
            </div>
            <div className={styles.proceedButton}>
              <AnalyticsHandler eventName={AnalyticsEventNames.SUBMIT_SIGNUP_PREPOPULATED}>
                <Button
                  id="submit-button"
                  buttonClass="primary-green"
                  type="submit"
                  text={gettext('Activate My Account')}
                  isLoading={props.isLoading}
                  loadingText={gettext('Saving')}
                  disabled={!formik.values.terms || !formik.values.confirmUsage}
                />
              </AnalyticsHandler>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};
SignUpStep.propTypes = {
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func,
  updateFields: PropTypes.func,
  product: PropTypes.string,
  prepopulatedFields: PropTypes.object,
};

export default SignUpStep;
